/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {stringToArrayBuffer} from './http_utils';
import {URLSearchParams} from './url_search_params';
/**
 *  HTTP request body used by both {@link Request} and {@link Response}
  * https://fetch.spec.whatwg.org/#body
 * @abstract
 */
export abstract class Body {
  /**
   * @internal
   */
  protected _body: any;
/**
 *  Attempts to return body as parsed `JSON` object, or raises an exception.
 * @return {?}
 */
json(): any {
    if (typeof this._body === 'string') {
      return JSON.parse( /** @type {?} */((<string>this._body)));
    }

    if (this._body instanceof ArrayBuffer) {
      return JSON.parse(this.text());
    }

    return this._body;
  }
/**
 *  Returns the body as a string, presuming `toString()` can be called on the response body.
 * @return {?}
 */
text(): string {
    if (this._body instanceof URLSearchParams) {
      return this._body.toString();
    }

    if (this._body instanceof ArrayBuffer) {
      return String.fromCharCode.apply(null, new Uint16Array( /** @type {?} */((<ArrayBuffer>this._body))));
    }

    if (this._body == null) {
      return '';
    }

    if (typeof this._body === 'object') {
      return JSON.stringify(this._body, null, 2);
    }

    return this._body.toString();
  }
/**
 *  Return the body as an ArrayBuffer
 * @return {?}
 */
arrayBuffer(): ArrayBuffer {
    if (this._body instanceof ArrayBuffer) {
      return /** @type {?} */(( <ArrayBuffer>this._body));
    }

    return stringToArrayBuffer(this.text());
  }
/**
 *  Returns the request's body as a Blob, assuming that body exists.
 * @return {?}
 */
blob(): Blob {
    if (this._body instanceof Blob) {
      return /** @type {?} */(( <Blob>this._body));
    }

    if (this._body instanceof ArrayBuffer) {
      return new Blob([this._body]);
    }

    throw new Error('The request body isn\'t either a blob or an array buffer');
  }
}

function Body_tsickle_Closure_declarations() {
 /** @type {?} */
Body.prototype._body;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
